import { InputNumber } from "@/components/InputNumber";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  datePickerStandardProps,
  quillEditorStandardProps,
  quillFormItemProps,
} from "@/utils/componentUtils";
import { earliestDate, toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Card, Checkbox, Col, DatePicker, Form, Row } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { FormInstance, Rule } from "antd/lib/form";
import ReactQuill from "react-quill-new";
import t from "../../../../../../app/i18n";
import LabelWithTooltip from "../../../../../../common/components/form/labels/LabelWithTooltip";
import { rowGutter } from "../../../../../../common/constants";
import { CreateUpdateContractGainerRecord, CreateUpdateDepositContract } from "../../../../types";
import { calculateContractStatus, calculateGainerRecordStartDates } from "../../../../utils";
import ContractStatusTag from "../../../tags/ContractStatusTag";

interface Props {
  form: FormInstance<CreateUpdateDepositContract>;
}

const DepositContractFormDataSection = ({ form }: Props) => {
  const handleSignDateChange = () => {
    handleStatusDefiningDateChange();
    const signDate = form.getFieldValue(["signDate"]) as string;
    const records = form.getFieldValue(["gainerRecords"]) as CreateUpdateContractGainerRecord[];
    form.setFieldsValue({ gainerRecords: calculateGainerRecordStartDates(records, signDate) });
  };

  const handleStatusDefiningDateChange = () => {
    const { signDate, effectiveEndDate, cancellationDate, transferredToOtherBrokerDate } = form.getFieldsValue([
      ["signDate"],
      ["effectiveEndDate"],
      ["cancellationDate"],
      ["transferredToOtherBrokerDate"],
    ]) as CreateUpdateDepositContract;
    form.setFieldsValue({
      status: calculateContractStatus({
        startDate: signDate,
        endDate: effectiveEndDate,
        cancellationDate: cancellationDate,
        transferredToOtherBrokerDate: transferredToOtherBrokerDate,
      }),
    });
  };

  const handleTransferredToOtherBrokerChange = (event: CheckboxChangeEvent) => {
    if (!event.target.checked) {
      form.setFieldsValue({ transferredToOtherBrokerDate: undefined });
      handleStatusDefiningDateChange();
    }
  };

  const colSpan = 4;

  return (
    <Card
      type="inner"
      className="card-box card-box--inner-extra"
      title={t("contract.sections.contractData")}
      extra={
        <Form.Item noStyle shouldUpdate={(prev, next) => prev.status !== next.status}>
          {({ getFieldValue }) => {
            const status = getFieldValue("status");
            return status ? <ContractStatusTag status={status} /> : undefined;
          }}
        </Form.Item>
      }
    >
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.signDate !== next.signDate}>
            {({ getFieldValue }) => {
              const signDate = getFieldValue("signDate") as string | undefined;
              const rules: Rule[] = [validations.notNull];
              if (signDate) {
                rules.push(validations.notAfter(signDate, t("contract.attrs.signDate")));
              }
              return (
                <Form.Item
                  name="mediationReportSignDate"
                  label={t("contract.attrs.mediationReportSignDate")}
                  rules={rules}
                  {...datePickerFormItemProps}
                >
                  <DatePicker {...datePickerStandardProps} maxDate={toDate(signDate)} />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="signDate"
            label={t("contract.attrs.signDate")}
            rules={[validations.notNull]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} onChange={handleSignDateChange} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.signDate !== next.signDate}>
            {({ getFieldValue }) => {
              const signDate = getFieldValue("signDate") as string | undefined;
              return (
                <Form.Item
                  name="effectiveEndDate"
                  label={t("contract.attrs.effectiveEndDate")}
                  rules={[
                    signDate ? validations.notSameOrBefore(signDate, t("contract.attrs.signDate")) : validations.none,
                  ]}
                  {...datePickerFormItemProps}
                >
                  <DatePicker
                    {...datePickerClearableProps}
                    minDate={toDate(signDate)?.add(1, "day")}
                    onChange={handleStatusDefiningDateChange}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.signDate !== next.signDate || prev.effectiveEndDate !== next.effectiveEndDate
            }
          >
            {({ getFieldValue }) => {
              const signDate = getFieldValue("signDate") as string | undefined;
              const effectiveEndDate = getFieldValue("effectiveEndDate") as string | undefined;
              const rules = [];
              if (signDate) {
                rules.push(validations.notBefore(signDate, t("contract.attrs.signDate")));
              }
              if (effectiveEndDate) {
                rules.push(validations.notSameOrAfter(effectiveEndDate, t("contract.attrs.effectiveEndDate")));
              }
              return (
                <Form.Item
                  name="cancellationDate"
                  label={t("contract.attrs.cancellationDate")}
                  rules={rules.length > 0 ? rules : [validations.none]}
                  {...datePickerFormItemProps}
                >
                  <DatePicker
                    {...datePickerClearableProps}
                    minDate={toDate(signDate)}
                    maxDate={toDate(effectiveEndDate)?.subtract(1, "day")}
                    onChange={handleStatusDefiningDateChange}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="targetAmount"
            label={t("contract.attrs.targetAmount")}
            rules={[validations.notNull, validations.minNumber(0)]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="transferredFromOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredFromOtherBroker")}
                tooltip={t("contract.helpers.transferredFromOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="transferredToOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }} onChange={handleTransferredToOtherBrokerChange}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredToOtherBroker")}
                tooltip={t("contract.helpers.transferredToOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.signDate !== next.signDate ||
              prev.effectiveEndDate !== next.effectiveEndDate ||
              prev.cancellationDate !== next.cancellationDate ||
              prev.transferredToOtherBroker !== next.transferredToOtherBroker
            }
          >
            {({ getFieldValue }) => {
              const signDate = getFieldValue("signDate") as string | undefined;
              const endDate = getFieldValue("effectiveEndDate") as string | undefined;
              const cancellationDate = getFieldValue("cancellationDate") as string | undefined;
              const transferredToOtherBroker = getFieldValue("transferredToOtherBroker");
              const rules = [];
              if (signDate) {
                rules.push(validations.notSameOrBefore(signDate, t("contract.attrs.signDate")));
              }
              if (cancellationDate) {
                rules.push(validations.notAfter(cancellationDate, t("contract.attrs.cancellationDate")));
              } else if (endDate) {
                rules.push(validations.notAfter(endDate, t("contract.attrs.effectiveEndDate")));
              }
              if (transferredToOtherBroker) {
                rules.push(validations.notNull);
              }
              return (
                <Form.Item
                  name="transferredToOtherBrokerDate"
                  label={
                    <LabelWithTooltip
                      label={t("contract.attrs.transferredToOtherBrokerDate")}
                      tooltip={t("contract.helpers.transferredToOtherBrokerDateDesc")}
                    />
                  }
                  rules={rules.length > 0 ? rules : [validations.none]}
                  {...datePickerFormItemProps}
                >
                  <DatePicker
                    {...datePickerClearableProps}
                    disabled={!transferredToOtherBroker}
                    minDate={toDate(signDate)?.add(1, "day")}
                    maxDate={earliestDate(cancellationDate, endDate)}
                    onChange={handleStatusDefiningDateChange}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter} className="margin-top-small">
        <Col span={24}>
          <Form.Item
            name="note"
            label={t("contract.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};

export default DepositContractFormDataSection;
